// src/pages/client/HomePage.jsx

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Search,
  Star,
  MapPin,
  ArrowRight,
  Award,
  Users,
  Globe,
  CheckCircle,
  Wrench,
  Home,
  Briefcase,
  Tool,
  PaintBucket,
  Scissors,
  Droplet,
  Zap,
  Flower,
  Sparkles,
  Mail,
  Facebook,
  Instagram,
  Twitter,
  Youtube,
  ChevronRight,
  X,
  Navigation
} from 'lucide-react';

import { useLanguage } from '../../contexts/LanguageContext';
import useTranslation from '../../hooks/useTranslation';
import useIPLocation from '../../hooks/useIPLocation';
import colors from '../../constants/colors';
import {
  getAllProviders,
  selectAllProviders,
  selectProviderLoading,
  selectProviderError,
  selectProviderById,
  setFilters,
  clearFilters
} from '../../store/slices/providerSlice';

import styles from '../../styles/HomePage.module.css';

// ======================================================
// CONSTANTS
// ======================================================
const MIN_RATING_FOR_TOP_RATED = 3;
const MAX_DISTANCE_NEAR_ME = 100;

const HAITIAN_CITIES = [
  'port-au-prince', 'cap-haïtien', 'cap-haitien', 'jacmel', 'gonaïves', 'gonaives',
  'les cayes', 'jérémie', 'jeremie', 'hinche', 'port-de-paix', 'petit-goâve',
  'petit-goave', 'miragôane', 'miragowane', 'saint-marc', 'saint marc',
  'croix-des-bouquets', 'croix des bouquets', 'delmas', 'pétion-ville', 'pétionville',
  'carrefour', 'kenscoff', 'leogâne', 'leogane', 'grand-goâve', 'grand-goave'
];

// ======================================================
// SEPARATE COMPONENT FOR CATEGORY ITEM
// ======================================================
const CategoryItem = ({ item, onPress }) => {
  const { translatedText: categoryName } = useTranslation(item.name);
  const { translatedText: providersLabel } = useTranslation('providers');
  
  return (
    <div 
      className={styles.categoryCard}
      onClick={() => onPress(item.name)}
    >
      <div className={styles.categoryIcon} style={{ backgroundColor: item.color + '20' }}>
        <span className={styles.categoryEmoji}>{item.icon}</span>
      </div>
      <div className={styles.categoryName}>{categoryName}</div>
      <div className={styles.categoryCount}>{item.providerCount || 0} {providersLabel}</div>
    </div>
  );
};

// ======================================================
// SEPARATE COMPONENT FOR PROVIDER ITEM (NO HOURLY PRICE)
// ======================================================
const ProviderItem = ({ item, onPress, haitiLabel, diasporaLabel }) => {
  const { translatedText: translatedServiceType } = useTranslation(item?.serviceType || '');
  
  if (!item) return null;

  const isInHaiti = item.isInHaiti || false;
  const locationDisplay = isInHaiti 
    ? '🇭🇹 ' + haitiLabel
    : `🌍 ${item.address?.city || item.address?.country || diasporaLabel}`;

  const imageUrl = item.profileImage?.url || 'https://via.placeholder.com/80';

  return (
    <div 
      className={styles.providerCard}
      onClick={() => onPress(item)}
    >
      <img 
        src={imageUrl} 
        alt={item.businessName}
        className={styles.providerImage}
      />
      <div className={styles.providerInfo}>
        <div className={styles.providerHeader}>
          <span className={styles.providerName}>{item.businessName || item.name || 'Unknown Provider'}</span>
        </div>
        <div className={styles.providerService}>{translatedServiceType || item.serviceType || 'Service'}</div>
        <div className={styles.providerRating}>
          <Star size={14} color={colors.accentGold} />
          <span className={styles.ratingText}>{item.rating || 0}</span>
          <span className={styles.reviewText}>({item.reviewCount || 0})</span>
          {item.distanceDisplay && item.distanceDisplay !== 'N/A' && (
            <div className={styles.distanceBadge}>
              <MapPin size={12} color={colors.primary} />
              <span className={styles.distanceText}>{item.distanceDisplay}</span>
            </div>
          )}
        </div>
        <div className={styles.providerFooter}>
          <div className={styles.locationTag}>
            <span className={styles.locationTagText}>{locationDisplay}</span>
          </div>
          {item.yearsOfExperience > 0 && (
            <span className={styles.experienceText}>{item.yearsOfExperience} yrs exp</span>
          )}
        </div>
      </div>
    </div>
  );
};

// ======================================================
// LOCATION FILTER COMPONENT
// ======================================================
const LocationFilter = ({ selected, onSelect, locationString, providerCount }) => {
  const { translatedText: allLabel } = useTranslation('All');
  const { translatedText: haitiLabel } = useTranslation('Haiti');
  const { translatedText: diasporaLabel } = useTranslation('Diaspora');
  const { translatedText: nearMe } = useTranslation('Near Me');

  return (
    <div className={styles.locationFilterContainer}>
      <button
        className={`${styles.locationFilterBtn} ${selected === 'All' ? styles.active : ''}`}
        onClick={() => onSelect('All')}
      >
        🌍 {allLabel}
      </button>
      <button
        className={`${styles.locationFilterBtn} ${selected === 'Haiti' ? styles.active : ''}`}
        onClick={() => onSelect('Haiti')}
      >
        🇭🇹 {haitiLabel}
      </button>
      <button
        className={`${styles.locationFilterBtn} ${selected === 'Diaspora' ? styles.active : ''}`}
        onClick={() => onSelect('Diaspora')}
      >
        🌍 {diasporaLabel}
      </button>
      <button
        className={`${styles.locationFilterBtn} ${selected === 'Near Me' ? styles.active : ''}`}
        onClick={() => onSelect('Near Me')}
      >
        <Navigation size={16} className={styles.nearMeIcon} />
        {nearMe}
        {selected === 'Near Me' && providerCount !== undefined && (
          <span className={styles.providerCountBadge}>{providerCount}</span>
        )}
      </button>
    </div>
  );
};

// ======================================================
// SERVICE CATEGORIES
// ======================================================
const serviceCategories = [
  { id: '1', name: 'Electrician', icon: '⚡', color: '#FFD700' },
  { id: '2', name: 'Plumber', icon: '🔧', color: '#4A90D9' },
  { id: '3', name: 'Mason', icon: '🧱', color: '#D35400' },
  { id: '4', name: 'Painter', icon: '🎨', color: '#2ECC71' },
  { id: '5', name: 'Carpenter', icon: '🪚', color: '#E67E22' },
  { id: '6', name: 'Barber', icon: '💈', color: '#9B59B6' },
  { id: '7', name: 'Hairdresser', icon: '💇', color: '#E74C3C' },
  { id: '8', name: 'Mechanic', icon: '🔩', color: '#3498DB' },
  { id: '9', name: 'HVAC Technician', icon: '❄️', color: '#1ABC9C' },
  { id: '10', name: 'Cleaner', icon: '🧹', color: '#F39C12' },
];

// ======================================================
// MAIN HOMEPAGE COMPONENT
// ======================================================
const HomePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Redux state
  const providers = useSelector(selectAllProviders) || [];
  const isLoading = useSelector(selectProviderLoading) || false;
  const error = useSelector(selectProviderError) || null;
  
  // IP Location hook
  const {
    position: ipLocation,
    error: locationError,
    loading: locationLoading,
    source: locationSource,
    isFallback: isFallbackLocation,
    hasLocation: hasIPLocation,
    locationString,
    refreshLocation,
    formatLocation,
    getDistance
  } = useIPLocation({
    autoLoad: true,
    maxRetries: 2,
    retryDelay: 1000,
    cacheEnabled: true
  });

  // Local state
  const [refreshing, setRefreshing] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLocationReady, setIsLocationReady] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const { currentLanguage } = useLanguage();

  // ======================================================
  // TRANSLATIONS
  // ======================================================
  const { translatedText: homeTitle } = useTranslation('Haitian Services Platform');
  const { translatedText: needService } = useTranslation('Need a Service?');
  const { translatedText: heroSubtitle } = useTranslation('Connect with trusted Haitian technicians near you');
  const { translatedText: findTechnician } = useTranslation('Find a Technician');
  const { translatedText: browseServices } = useTranslation('Browse Services');
  const { translatedText: seeAll } = useTranslation('See All');
  const { translatedText: nearestToYou } = useTranslation('Nearest to You');
  const { translatedText: topRated } = useTranslation('Top Rated');
  const { translatedText: haitianServicesCommunity } = useTranslation('Haitian Services Community');
  const { translatedText: inHaiti } = useTranslation('In Haiti');
  const { translatedText: inDiaspora } = useTranslation('In Diaspora');
  const { translatedText: totalProviders } = useTranslation('Total Providers');
  const { translatedText: connectingHaitians } = useTranslation('Connecting Haitians Worldwide, One Service at a Time');
  const { translatedText: madeWithLove } = useTranslation('All rights reserved');
  const { translatedText: providersLabel } = useTranslation('providers');
  const { translatedText: allLabel } = useTranslation('All');
  const { translatedText: haitiLabel } = useTranslation('Haiti');
  const { translatedText: diasporaLabel } = useTranslation('Diaspora');
  const { translatedText: searchPlaceholder } = useTranslation('Search for services, technicians, or locations...');
  const { translatedText: nearMe } = useTranslation('Near Me');
  const { translatedText: noProvidersFound } = useTranslation('No providers found in your area');

  // ======================================================
  // Set location ready when location is loaded
  // ======================================================
  useEffect(() => {
    if (hasIPLocation || locationError) {
      setIsLocationReady(true);
    }
  }, [hasIPLocation, locationError]);

  // ======================================================
  // FIX 1: Do NOT automatically select "Near Me"
  // Just mark that location is loaded
  // ======================================================
  useEffect(() => {
    if (hasIPLocation && isInitialLoad) {
      console.log('📍 Location detected');
      setIsInitialLoad(false);
    }
  }, [hasIPLocation, isInitialLoad]);

  // ======================================================
  // FETCH PROVIDERS - REMOVED verificationStatus filter
  // ======================================================
  const fetchProviders = useCallback(async () => {
    try {
      console.log('📌 Fetching providers...');
      
      const filters = {
        limit: 100,
        page: 1
      };

      const result = await dispatch(getAllProviders(filters)).unwrap();
      console.log('✅ Providers fetched successfully:', result);
      
      if (result?.data?.items) {
        console.log(`📊 Total providers loaded: ${result.data.items.length}`);
      }

    } catch (error) {
      console.error('❌ Error fetching providers:', error);
    }
  }, [dispatch]);

  // ======================================================
  // INITIAL LOAD
  // ======================================================
  useEffect(() => {
    const init = async () => {
      try {
        await fetchProviders();
      } catch (error) {
        console.error('❌ Initial load failed:', error);
      }
    };

    init();
  }, [fetchProviders]);

  // ======================================================
  // REFRESH HANDLER
  // ======================================================
  const handleRefresh = async () => {
    if (refreshing) return;

    setRefreshing(true);

    try {
      await refreshLocation();
      await fetchProviders();
    } catch (error) {
      console.error('❌ Refresh failed:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // ======================================================
  // HANDLE LOCATION FILTER CHANGE
  // ======================================================
  const handleLocationFilter = useCallback((location) => {
    if (location === selectedLocation) return;

    if (location === 'Near Me') {
      if (!hasIPLocation || !ipLocation) {
        alert('Please enable location services to use "Near Me" feature.');
        return;
      }
    }

    console.log(`🔄 Changing filter: ${selectedLocation} → ${location}`);
    setSelectedLocation(location);
  }, [selectedLocation, hasIPLocation, ipLocation]);

  // ======================================================
  // CALCULATE DISTANCE
  // ======================================================
  const calculateDistance = useCallback((lat1, lon1, lat2, lon2) => {
    const R = 3959; // Miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }, []);

  // ======================================================
  // CHECK IF PROVIDER IS IN HAITI
  // ======================================================
  const isProviderInHaiti = useCallback((provider) => {
    if (!provider || !provider.address) return false;
    
    const country = provider.address.country?.toLowerCase() || '';
    const city = provider.address.city?.toLowerCase() || '';
    
    if (country === 'haiti' || country === 'ht' || country === 'haïti') {
      return true;
    }
    
    if (HAITIAN_CITIES.some(haitianCity => city.includes(haitianCity))) {
      return true;
    }
    
    return false;
  }, []);

  // ======================================================
  // PROCESS PROVIDERS WITH DISTANCE
  // ======================================================
  const processedProviders = useMemo(() => {
    console.log('🔄 Processing providers...', providers?.length || 0);
    
    if (!Array.isArray(providers) || providers.length === 0) {
      console.log('⚠️ No providers to process');
      return [];
    }

    const processed = providers
      .filter(provider => provider !== null && provider !== undefined)
      .map(provider => {
        let distance = null;
        let distanceDisplay = 'N/A';
        let hasValidCoords = false;

        const coords = provider.location?.coordinates;
        
        if (coords && Array.isArray(coords) && coords.length === 2 && ipLocation) {
          const [lng, lat] = coords;
          if (typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng)) {
            hasValidCoords = true;
            try {
              distance = calculateDistance(
                ipLocation.latitude,
                ipLocation.longitude,
                lat,
                lng
              );
              distanceDisplay = distance < 0.1
                ? '< 0.1 mi'
                : distance < 1
                  ? `${(distance * 5280).toFixed(0)} ft`
                  : `${distance.toFixed(1)} mi`;
            } catch (err) {
              console.warn('Distance calculation error:', err);
            }
          }
        }

        const isInHaiti = isProviderInHaiti(provider);

        return {
          ...provider,
          distance,
          distanceDisplay,
          hasValidCoords,
          isInHaiti
        };
      });

    console.log(`✅ Processed ${processed.length} providers`);
    return processed;
  }, [providers, ipLocation, calculateDistance, isProviderInHaiti]);

  // ======================================================
  // FILTER PROVIDERS
  // ======================================================
  const filteredProviders = useMemo(() => {
    console.log('🔄 Filtering providers with filter:', selectedLocation);
    
    if (!Array.isArray(processedProviders) || processedProviders.length === 0) {
      console.log('⚠️ No providers to filter');
      return [];
    }

    let filtered = [...processedProviders];

    if (selectedLocation === 'Haiti') {
      filtered = filtered.filter(p => p.isInHaiti === true);
      console.log(`🇭🇹 Filtered to ${filtered.length} providers in Haiti`);
    } else if (selectedLocation === 'Diaspora') {
      filtered = filtered.filter(p => p.isInHaiti === false);
      console.log(`🌍 Filtered to ${filtered.length} providers in Diaspora`);
    } else if (selectedLocation === 'Near Me') {
      filtered = filtered.filter(p => {
        const isValid = p.hasValidCoords && p.distance !== null && p.distance <= MAX_DISTANCE_NEAR_ME;
        return isValid;
      });
      filtered = filtered.sort((a, b) => (a.distance || 999) - (b.distance || 999));
      console.log(`📍 Near Me found ${filtered.length} providers within ${MAX_DISTANCE_NEAR_ME} miles`);
    } else {
      console.log(`🌍 Showing all ${filtered.length} providers`);
    }

    return filtered;
  }, [processedProviders, selectedLocation]);

  // ======================================================
  // GET NEAREST PROVIDERS
  // ======================================================
  const nearestProviders = useMemo(() => {
    console.log('🔄 Calculating nearest providers...');
    
    if (!Array.isArray(filteredProviders) || filteredProviders.length === 0) {
      console.log('⚠️ No providers for nearest calculation');
      return [];
    }

    const sorted = [...filteredProviders]
      .sort((a, b) => {
        if (a.distance !== null && b.distance !== null) {
          return a.distance - b.distance;
        }
        if (a.distance !== null) return -1;
        if (b.distance !== null) return 1;
        return 0;
      })
      .slice(0, 6);

    console.log(`📍 Nearest providers: ${sorted.length} found`);
    return sorted;
  }, [filteredProviders]);

  // ======================================================
  // GET TOP RATED PROVIDERS
  // ======================================================
  const topRatedProviders = useMemo(() => {
    console.log('🔄 Calculating top rated providers...');
    
    if (!Array.isArray(filteredProviders) || filteredProviders.length === 0) {
      console.log('⚠️ No providers for top rated calculation');
      return [];
    }
    
    const sorted = [...filteredProviders]
      .filter(p => typeof p.rating === 'number' && p.rating >= MIN_RATING_FOR_TOP_RATED)
      .sort((a, b) => (b.rating || 0) - (a.rating || 0))
      .slice(0, 6);

    console.log(`⭐ Top rated providers: ${sorted.length} found`);
    return sorted;
  }, [filteredProviders]);

  // ======================================================
  // UPDATE CATEGORY COUNTS
  // ======================================================
  const categoriesWithCounts = useMemo(() => {
    if (!Array.isArray(filteredProviders) || filteredProviders.length === 0) {
      return serviceCategories.map(cat => ({ ...cat, providerCount: 0 }));
    }
    
    return serviceCategories.map(category => {
      const count = filteredProviders.filter(p => p.serviceType === category.name).length;
      return { ...category, providerCount: count };
    });
  }, [filteredProviders]);

  // ======================================================
  // GET STATS
  // ======================================================
  const haitiCount = useMemo(() => {
    return filteredProviders.filter(p => p.isInHaiti === true).length;
  }, [filteredProviders]);

  const diasporaCount = useMemo(() => {
    return filteredProviders.filter(p => p.isInHaiti === false).length;
  }, [filteredProviders]);

  // ======================================================
  // GET NEAR ME MESSAGE
  // ======================================================
  const getNearMeMessage = () => {
    if (!hasIPLocation) return 'Detecting your location...';
    if (selectedLocation === 'Near Me') {
      const count = filteredProviders.length;
      if (count === 0) {
        return `No providers found within ${MAX_DISTANCE_NEAR_ME} miles of ${ipLocation?.country || 'your area'}`;
      }
      return `${count} providers found near you in ${ipLocation?.country || 'your area'}`;
    }
    return '';
  };

  // ======================================================
  // NAVIGATION HANDLERS
  // ======================================================
  const handleSearch = (e) => { 
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate('/providers', { state: { search: searchQuery } });
    }
  };

  const handleCategoryPress = (categoryName) => {
    navigate('/providers', { state: { category: categoryName } });
  };

  const handleProviderPress = (provider) => {
    navigate(`/provider-details/${provider.id}`, { state: { provider } });
  };

  const handleFindTechnician = () => {
    navigate('/providers');
  };

  const handleSeeAllNearest = () => {
    navigate('/providers', { state: { filter: 'nearest' } });
  };

  const handleSeeAllTopRated = () => {
    navigate('/providers', { state: { filter: 'top_rated' } });
  };

  const handleBrowseServices = () => {
    navigate('/providers');
  };

  // ======================================================
  // RENDER FUNCTIONS
  // ======================================================
  const renderCategoryItem = (item) => (
    <CategoryItem 
      key={item.id}
      item={item} 
      onPress={handleCategoryPress}
    />
  );

  const renderProviderItem = (item) => (
    <ProviderItem 
      key={item.id}
      item={item} 
      onPress={handleProviderPress}
      haitiLabel={haitiLabel}
      diasporaLabel={diasporaLabel}
    />
  );

  // ======================================================
  // LOADING STATE
  // ======================================================
  if ((locationLoading || isLoading) && !isLocationReady) {
    return (
      <div className={styles.homeContainer}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>Detecting your location...</p>
        </div>
      </div>
    );
  }

  // ======================================================
  // MAIN RENDER
  // ======================================================
  return (
    <div className={styles.homeContainer}>
      {/* Hero Banner */}
      <div className={styles.heroBanner}>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>
            {needService}
          </h1>
          <p className={styles.heroSubtitle}>{heroSubtitle}</p>
          <div className={styles.heroButtons}>
            <button className={styles.heroButton} onClick={handleFindTechnician}>
              <span>{findTechnician}</span>
              <ArrowRight size={20} />
            </button>
            <button className={styles.heroButtonSecondary} onClick={handleBrowseServices}>
              <span>{browseServices}</span>
            </button>
          </div>
          <div className={styles.heroStats}>
            <div className={styles.heroStat}>
              <span className={styles.heroStatNumber}>{filteredProviders.length}+</span>
              <span className={styles.heroStatLabel}>{totalProviders}</span>
            </div>
            <div className={styles.heroStat}>
              <span className={styles.heroStatNumber}>4.8⭐</span>
              <span className={styles.heroStatLabel}>Avg Rating</span>
            </div>
            <div className={styles.heroStat}>
              <span className={styles.heroStatNumber}>100%</span>
              <span className={styles.heroStatLabel}>Verified</span>
            </div>
          </div>
        </div>
        <div className={styles.heroEmoji}>
          <span className={styles.heroEmojiText}>🔧</span>
        </div>
      </div>

      {/* Search Bar */}
      <div className={styles.searchBarContainer}>
        <form className={styles.searchBar} onSubmit={handleSearch}>
          <Search className={styles.searchIcon} size={20} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button type="submit" className={styles.searchButton}>
            Search
          </button>
        </form>
      </div>

      {/* Location Status Bar */}
      {hasIPLocation && ipLocation && (
        <div className={styles.locationStatusBar}>
          <span className={styles.locationStatusIcon}>📍</span>
          <span className={styles.locationStatusText}>
            Location detected: {ipLocation.country || 'Unknown'}
            {isFallbackLocation && ' ⚠️'}
          </span>
          {selectedLocation === 'Near Me' && (
            <span className={styles.locationStatusBadge}>
              {filteredProviders.length} providers
            </span>
          )}
        </div>
      )}

      {/* Main Content */}
      <div className={styles.mainContent}>
        {/* Location Filter with Near Me option */}
        <LocationFilter 
          selected={selectedLocation}
          onSelect={handleLocationFilter}
          locationString={locationString}
          providerCount={selectedLocation === 'Near Me' ? filteredProviders.length : undefined}
        />

        {/* Near Me Info - Only show when Near Me is selected */}
        {selectedLocation === 'Near Me' && (
          <div className={styles.nearMeInfo}>
            <span className={styles.nearMeInfoIcon}>📍</span>
            <span className={styles.nearMeInfoText}>
              {getNearMeMessage()}
            </span>
            {hasIPLocation && (
              <button 
                className={`${styles.nearMeRefresh} ${refreshing ? styles.spinning : ''}`}
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <span className={styles.refreshIcon}>🔄</span>
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </button>
            )}
          </div>
        )}

        {/* Service Categories */}
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              <span className={styles.titleIcon}>📋</span>
              {browseServices}
            </h2>
            <button className={styles.seeAllBtn} onClick={handleBrowseServices}>
              {seeAll} <ChevronRight size={16} />
            </button>
          </div>
          <div className={styles.categoriesGrid}>
            {categoriesWithCounts.map(renderCategoryItem)}
          </div>
        </div>

        {/* Nearest Technicians */}
        {isLoading ? (
          <div className={styles.sectionContainer}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>
                <span className={styles.titleIcon}>📍</span>
                {nearestToYou}
              </h2>
            </div>
            <div className={styles.loadingContainer}>
              <div className={styles.spinner}></div>
              <p className={styles.loadingText}>Loading providers...</p>
            </div>
          </div>
        ) : nearestProviders.length > 0 ? (
          <div className={styles.sectionContainer}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>
                <span className={styles.titleIcon}>📍</span>
                {nearestToYou}
              </h2>
              <button className={styles.seeAllBtn} onClick={handleSeeAllNearest}>
                {seeAll} <ChevronRight size={16} />
              </button>
            </div>
            <div className={styles.providersGrid}>
              {nearestProviders.map(renderProviderItem)}
            </div>
          </div>
        ) : (
          <div className={styles.sectionContainer}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>
                <span className={styles.titleIcon}>📍</span>
                {nearestToYou}
              </h2>
            </div>
            <div className={styles.emptyStateContainer}>
              <p className={styles.emptyStateText}>No providers found</p>
            </div>
          </div>
        )}

        {/* Top Rated */}
        {isLoading ? (
          <div className={styles.sectionContainer}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>
                <span className={styles.titleIcon}>⭐</span>
                {topRated}
              </h2>
            </div>
            <div className={styles.loadingContainer}>
              <div className={styles.spinner}></div>
              <p className={styles.loadingText}>Loading top rated...</p>
            </div>
          </div>
        ) : topRatedProviders.length > 0 ? (
          <div className={styles.sectionContainer}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>
                <span className={styles.titleIcon}>⭐</span>
                {topRated}
              </h2>
              <button className={styles.seeAllBtn} onClick={handleSeeAllTopRated}>
                {seeAll} <ChevronRight size={16} />
              </button>
            </div>
            <div className={styles.providersGrid}>
              {topRatedProviders.map(renderProviderItem)}
            </div>
          </div>
        ) : (
          <div className={styles.sectionContainer}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>
                <span className={styles.titleIcon}>⭐</span>
                {topRated}
              </h2>
            </div>
            <div className={styles.emptyStateContainer}>
              <p className={styles.emptyStateText}>No providers with {MIN_RATING_FOR_TOP_RATED}+ stars yet</p>
            </div>
          </div>
        )}

        {/* Community Stats - Full Width */}
        <div className={styles.statsContainerFull}>
          <div className={styles.statsContent}>
            <h2 className={styles.statsTitle}>🇭🇹 {haitianServicesCommunity}</h2>
            <div className={styles.statsGridFull}>
              <div className={styles.statItemFull}>
                <div className={styles.statIcon}>🇭🇹</div>
                <div className={styles.statNumberFull}>{haitiCount}</div>
                <div className={styles.statLabelFull}>{inHaiti}</div>
              </div>
              <div className={styles.statItemFull}>
                <div className={styles.statIcon}>🌍</div>
                <div className={styles.statNumberFull}>{diasporaCount}</div>
                <div className={styles.statLabelFull}>{inDiaspora}</div>
              </div>
              <div className={styles.statItemFull}>
                <div className={styles.statIcon}>👥</div>
                <div className={styles.statNumberFull}>{filteredProviders.length}</div>
                <div className={styles.statLabelFull}>{totalProviders}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer - Full Width */}
      <footer className={styles.footerFull}>
        <div className={styles.footerContent}>
          <div className={styles.footerGridFull}>
            {/* Brand Column */}
            <div className={styles.footerBrandColumn}>
              <div className={styles.footerBrandContainer}>
                <div className={styles.footerLogoWrapper}>
                  <img 
                    src="/logo/logo_hsm.png" 
                    alt="Haitian Services"
                    className={styles.footerLogo}
                  />
                </div>
                <div>
                  <div className={styles.footerBrandName}>{homeTitle}</div>
                  <div className={styles.footerBrandDescription}>{connectingHaitians}</div>
                </div>
              </div>
              <div className={styles.footerSocialLinks}>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-facebook.svg" alt="Facebook" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-instagram.svg" alt="Instagram" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-twitter.svg" alt="Twitter" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-youtube.svg" alt="YouTube" width="20" height="20" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>Quick Links</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/providers">Find Providers</a></li>
                <li><a href="/donation">Support Us</a></li>
                <li><a href="/help-support">Help & Support</a></li>
                <li><a href="/about">About Us</a></li>
              </ul>
            </div>

            {/* For Providers */}
            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>For Providers</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/register">Become a Provider</a></li>
                <li><a href="/login">Provider Login</a></li>
                <li><a href="/provider-dashboard">Dashboard</a></li>
                <li><a href="/provider-settings">Settings</a></li>
              </ul>
            </div>

            {/* Legal */}
            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>Legal</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/terms">Terms of Service</a></li>
                <li><a href="/privacy">Privacy Policy</a></li>
                <li><a href="/cookies">Cookie Policy</a></li>
              </ul>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className={styles.footerDividerFull}></div>
          <div className={styles.footerBottomFull}>
            <div className={styles.footerFlag}>
              <div className={styles.flagStripes}>
                <div className={`${styles.stripe} ${styles.stripeBlue}`}></div>
                <div className={`${styles.stripe} ${styles.stripeRed}`}></div>
              </div>
            </div>
            <div className={styles.footerBottomText}>
              © 2026 {homeTitle}, {madeWithLove}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;